
import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    question: string;
    correct: boolean;
}


const BodyPartsTrueFalse: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'BodyPartsTrueFalse.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/BodyPartsTrueFalse.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "Parts of Body True or False Questions for UKG | Body Awareness for Kids"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: boolean) => {
        console.log(selectedMyOption)
        const currentQuestion = questions[currentIndex];
        setSelectedOption(selectedMyOption);
        if (selectedMyOption === currentQuestion.correct) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
                setSelectedOption(null)
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG kids body parts using fun true or false questions. Develop critical thinking and vocabulary with printable body parts worksheets for UKG." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/parts-of-body/true-false" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord" style={{ marginTop: "0%" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Parts of the Body</h1>

                                    <p>
                                        Learning about body parts is a key part of early education. This <strong>True or False activity</strong> helps UKG students identify body parts and understand their basic functions through simple and engaging statements.
                                    </p>

                                    <h2>👦 Why True or False Questions Help UKG Learners</h2>
                                    <ul>
                                        <li>Encourages logical thinking</li>
                                        <li>Strengthens knowledge of body parts</li>
                                        <li>Promotes confidence in making decisions</li>
                                    </ul>

                                    <h2>✅ Sample True or False Questions</h2>
                                    <ul>
                                        <li>We use our ears to hear. <strong>(True)</strong></li>
                                        <li>We walk with our hands. <strong>(False)</strong></li>
                                        <li>The eyes help us see. <strong>(True)</strong></li>
                                        <li>We taste with our nose. <strong>(False)</strong></li>
                                        <li>Our legs help us jump. <strong>(True)</strong></li>
                                    </ul>




                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Sense Organs</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-home"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My Home</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-school"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My School</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Recognize and name major body parts</li>
                                        <li>Understand basic body functions</li>
                                        <li>Practice decision-making through true/false logic</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                                    <ul>
                                        <li>Read the statements aloud and let the child respond</li>
                                        <li>Use real-life actions like pointing or moving body parts to demonstrate answers</li>
                                        <li>Use visual aids like body diagrams or flashcards</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Parts of the Body</h1>

                                <p>
                                    Learning about body parts is a key part of early education. This <strong>True or False activity</strong> helps UKG students identify body parts and understand their basic functions through simple and engaging statements.
                                </p>

                                <h2>👦 Why True or False Questions Help UKG Learners</h2>
                                <ul>
                                    <li>Encourages logical thinking</li>
                                    <li>Strengthens knowledge of body parts</li>
                                    <li>Promotes confidence in making decisions</li>
                                </ul>

                                <h2>✅ Sample True or False Questions</h2>
                                <ul>
                                    <li>We use our ears to hear. <strong>(True)</strong></li>
                                    <li>We walk with our hands. <strong>(False)</strong></li>
                                    <li>The eyes help us see. <strong>(True)</strong></li>
                                    <li>We taste with our nose. <strong>(False)</strong></li>
                                    <li>Our legs help us jump. <strong>(True)</strong></li>
                                </ul>




                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize and name major body parts</li>
                                    <li>Understand basic body functions</li>
                                    <li>Practice decision-making through true/false logic</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                                <ul>
                                    <li>Read the statements aloud and let the child respond</li>
                                    <li>Use real-life actions like pointing or moving body parts to demonstrate answers</li>
                                    <li>Use visual aids like body diagrams or flashcards</li>
                                </ul>
                            </article>
                            <div className="mt-2 mb-2">
                                <h3 >
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>

                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value!)}>
                                <IonItem
                                    className={`grid-item ${selectedOption === true
                                        ? true === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={true} />
                                    <IonLabel>True</IonLabel>
                                </IonItem>
                                <IonItem
                                    className={`grid-item ${selectedOption === false
                                        ? false === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={false} />
                                    <IonLabel>False</IonLabel>
                                </IonItem>
                            </IonRadioGroup>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default BodyPartsTrueFalse;
